import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, MapPin, CalendarRange, Users as UsersIcon, ClipboardList, Boxes, Trash2, PartyPopper, FileText, ListChecks } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import EventChecklist from "../components/EventChecklist";
import { useLang } from "../context/LangContext";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const STATUS_TINT = {
  upcoming: "border-indigo-400/30 bg-indigo-400/10 text-indigo-200",
  ongoing: "border-emerald-400/30 bg-emerald-400/10 text-emerald-200",
  done: "border-white/10 bg-white/5 text-slate-300",
  cancelled: "border-rose-400/30 bg-rose-400/10 text-rose-200",
};

export default function Events() {
  const { t } = useLang();
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [params, setParams] = useSearchParams();

  const load = useCallback(() => api.get("/events").then((r) => setEvents(r.data)).catch(() => {}), []);
  useEffect(() => { load(); api.get("/users").then((r) => setUsers(r.data)).catch(() => {}); }, [load]);
  useEffect(() => {
    const id = params.get("open");
    if (id && events.length) { const e = events.find((x) => x.id === id); if (e) { setSelected(e); setParams({}, { replace: true }); } }
  }, [events, params, setParams]);

  const shown = useMemo(() => (filter === "all" ? events : events.filter((e) => e.status === filter)), [events, filter]);
  const canManage = ["admin", "member"].includes(user?.role);

  return (
    <div className="space-y-6" data-testid="events-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ fontFamily: "Outfit" }}>{t("events")}</h1>
          <p className="text-slate-400 mt-1 text-sm">{events.length} · {t("events_hint")}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="event-filter-status" className="w-[160px] bg-white/5 border-white/10"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("all")}</SelectItem>
              {["upcoming", "ongoing", "done", "cancelled"].map((s) => <SelectItem key={s} value={s}>{t(`ev_${s}`)}</SelectItem>)}
            </SelectContent>
          </Select>
          {canManage && <CreateEventDialog open={open} setOpen={setOpen} users={users} onCreated={load} />}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {shown.map((ev) => (
          <button key={ev.id} data-testid={`event-card-${ev.id}`} onClick={() => setSelected(ev)} className="text-left glass rounded-2xl p-5 hover:-translate-y-0.5 transition-transform">
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="font-semibold text-lg leading-tight" style={{ fontFamily: "Outfit" }}>{ev.name}</div>
              <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-md border ${STATUS_TINT[ev.status]}`}>{t(`ev_${ev.status}`)}</span>
            </div>
            <div className="text-xs text-slate-400 space-y-1">
              <div className="flex items-center gap-1.5"><CalendarRange className="w-3.5 h-3.5" />{ev.start_date}{ev.end_date !== ev.start_date ? ` → ${ev.end_date}` : ""}</div>
              {ev.location && <div className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" />{ev.location}</div>}
            </div>
            <div className="flex gap-2 mt-3 text-[11px] text-slate-300">
              <Badge variant="outline" className="border-white/10 bg-white/5"><UsersIcon className="w-3 h-3 mr-1" />{(ev.team || []).length}</Badge>
              <Badge variant="outline" className="border-white/10 bg-white/5"><ClipboardList className="w-3 h-3 mr-1" />{ev.orders_count}</Badge>
              <Badge variant="outline" className="border-white/10 bg-white/5"><Boxes className="w-3 h-3 mr-1" />{ev.reservations_count}</Badge>
              {ev.checklist_total > 0 && <Badge variant="outline" className={`border-white/10 ${ev.checklist_done === ev.checklist_total ? "bg-emerald-500/15 text-emerald-200" : "bg-white/5"}`}><ListChecks className="w-3 h-3 mr-1" />{ev.checklist_done}/{ev.checklist_total}</Badge>}
            </div>
          </button>
        ))}
        {shown.length === 0 && (
          <div className="col-span-full text-center text-slate-500 py-16 text-sm flex flex-col items-center gap-2"><PartyPopper className="w-6 h-6" />{t("no_data")}</div>
        )}
      </div>

      <EventSheet ev={selected} onClose={() => setSelected(null)} onChanged={load} users={users} canManage={canManage} isAdmin={user?.role === "admin"} currentUser={user} />
    </div>
  );
}

function TeamPicker({ users, value, onChange }) {
  const { t } = useLang();
  const creative = users.filter((u) => !["admin", "member"].includes(u.role));
  const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="event-team-picker">
      {creative.map((u) => (
        <button key={u.id} type="button" data-testid={`team-toggle-${u.id}`} onClick={() => toggle(u.id)}
          className={`text-xs px-2.5 py-1 rounded-lg border transition-colors ${value.includes(u.id) ? "bg-indigo-500/25 border-indigo-400/50 text-indigo-100" : "bg-white/5 border-white/10 text-slate-300 hover:bg-white/10"}`}>
          {u.name} <span className="text-[10px] opacity-70">· {t(u.role)}</span>
        </button>
      ))}
      {creative.length === 0 && <span className="text-xs text-slate-500">—</span>}
    </div>
  );
}

function CreateEventDialog({ open, setOpen, users, onCreated }) {
  const { t } = useLang();
  const today = new Date().toISOString().slice(0, 10);
  const empty = { name: "", description: "", location: "", start_date: today, end_date: today, team: [] };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/events", form);
      toast.success("OK"); setOpen(false); setForm(empty); onCreated();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="event-create-button" className="btn-glow bg-indigo-500 hover:bg-indigo-600"><Plus className="w-4 h-4 mr-1.5" />{t("new_event")}</Button>
      </DialogTrigger>
      <DialogContent className="bg-slate-900 border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{t("new_event")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>{t("name")}</Label><Input data-testid="event-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-white/5 border-white/10" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>{t("start_date")}</Label><Input data-testid="event-start-input" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value, end_date: form.end_date < e.target.value ? e.target.value : form.end_date })} className="mt-1 bg-white/5 border-white/10" /></div>
            <div><Label>{t("end_date")}</Label><Input data-testid="event-end-input" type="date" min={form.start_date} value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-1 bg-white/5 border-white/10" /></div>
          </div>
          <div><Label>{t("location")}</Label><Input data-testid="event-location-input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="mt-1 bg-white/5 border-white/10" /></div>
          <div><Label>{t("description")}</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 bg-white/5 border-white/10" /></div>
          <div><Label>{t("team")}</Label><div className="mt-1.5"><TeamPicker users={users} value={form.team} onChange={(team) => setForm({ ...form, team })} /></div></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>{t("cancel")}</Button>
          <Button data-testid="event-create-submit" onClick={submit} disabled={busy || !form.name} className="bg-indigo-500 hover:bg-indigo-600">{busy ? "…" : t("save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EventSheet({ ev, onClose, onChanged, users, canManage, isAdmin, currentUser }) {
  const { t } = useLang();
  const nav = useNavigate();
  const [full, setFull] = useState(null);
  const evId = ev?.id;
  const reload = useCallback(() => { if (evId) api.get(`/events/${evId}`).then((r) => setFull(r.data)); }, [evId]);
  useEffect(() => { if (!evId) { setFull(null); return; } reload(); }, [evId, reload]);
  if (!ev) return null;
  const e = full || ev;

  const patch = async (body) => { await api.patch(`/events/${ev.id}`, body); reload(); onChanged(); };
  const remove = async () => { await api.delete(`/events/${ev.id}`); onChanged(); onClose(); };
  const nameOf = (id) => users.find((u) => u.id === id)?.name || "?";
  const inTeam = (e.team || []).includes(currentUser?.id) || e.owner_id === currentUser?.id;
  const roadmapUrl = `${api.defaults.baseURL}/events/${ev.id}/roadmap?auth=${localStorage.getItem("access_token") || ""}`;

  return (
    <Sheet open={!!ev} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="bg-slate-900 border-white/10 w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader><SheetTitle className="pr-8">{e.name}</SheetTitle></SheetHeader>
        <div className="mt-4 space-y-5 text-sm">
          <div className="flex flex-wrap gap-2 items-center">
            <span className={`text-xs px-2 py-0.5 rounded-md border ${STATUS_TINT[e.status]}`}>{t(`ev_${e.status}`)}</span>
            <Badge variant="outline" className="border-white/10 bg-white/5"><CalendarRange className="w-3 h-3 mr-1" />{e.start_date}{e.end_date !== e.start_date ? ` → ${e.end_date}` : ""}</Badge>
            {e.location && <Badge variant="outline" className="border-white/10 bg-white/5"><MapPin className="w-3 h-3 mr-1" />{e.location}</Badge>}
            <a data-testid="event-roadmap-pdf" href={roadmapUrl} target="_blank" rel="noreferrer" className="ml-auto inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-lg bg-indigo-500/15 border border-indigo-400/30 text-indigo-200 hover:bg-indigo-500/25">
              <FileText className="w-3.5 h-3.5" />{t("roadmap_pdf")}
            </a>
          </div>
          {e.description && <p className="text-slate-300 whitespace-pre-wrap">{e.description}</p>}

          <EventChecklist event={e} onChanged={() => { reload(); onChanged(); }} canEdit={canManage || inTeam} canDelete={canManage} />

          {canManage && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-400">{t("status")}</Label>
                <Select value={e.status} onValueChange={(v) => patch({ status: v })}>
                  <SelectTrigger data-testid="event-status-select" className="mt-1 bg-white/5 border-white/10"><SelectValue /></SelectTrigger>
                  <SelectContent>{["upcoming", "ongoing", "done", "cancelled"].map((s) => <SelectItem key={s} value={s}>{t(`ev_${s}`)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-400">{t("owner")}</Label>
                <Select value={e.owner_id || "none"} onValueChange={(v) => patch({ owner_id: v })}>
                  <SelectTrigger className="mt-1 bg-white/5 border-white/10"><SelectValue /></SelectTrigger>
                  <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          )}

          <div>
            <div className="text-xs uppercase tracking-widest text-slate-400 mb-1.5">{t("team")}</div>
            {canManage ? <TeamPicker users={users} value={e.team || []} onChange={(team) => patch({ team })} /> : (
              <div className="flex flex-wrap gap-1.5">{(e.team || []).map((id) => <Badge key={id} variant="outline" className="border-white/10 bg-white/5">{nameOf(id)}</Badge>)}</div>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-xs uppercase tracking-widest text-slate-400">{t("orders")} · {(e.orders || []).length}</div>
              <button data-testid="event-view-orders" onClick={() => nav(`/orders?event=${ev.id}`)} className="text-xs text-indigo-300 hover:text-indigo-200">{t("view_details")} →</button>
            </div>
            <div className="space-y-1.5">
              {(e.orders || []).map((o) => (
                <button key={o.id} data-testid={`event-order-${o.id}`} onClick={() => nav(`/orders?open=${o.id}`)} className="w-full text-left glass rounded-lg px-3 py-2 flex items-center justify-between hover:bg-white/10">
                  <span className="truncate">{o.title}</span>
                  <span className="text-[10px] text-slate-400 whitespace-nowrap ml-2">{t(o.type)} · {t(o.status)}{o.deadline ? ` · ${o.deadline.slice(0, 10)}` : ""}</span>
                </button>
              ))}
              {(e.orders || []).length === 0 && <div className="text-xs text-slate-500">—</div>}
            </div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-widest text-slate-400 mb-1.5">{t("reserved_equipment")} · {(e.reservations || []).length}</div>
            <div className="space-y-1.5">
              {(e.reservations || []).map((r) => (
                <button key={r.id} data-testid={`event-reservation-${r.id}`} onClick={() => nav(`/equipment?open=${r.equipment_id}`)} className="w-full text-left glass rounded-lg px-3 py-2 flex items-center justify-between hover:bg-white/10">
                  <span>{r.equipment_name} ×{r.quantity}</span>
                  <span className="text-[10px] text-slate-400">{r.user_name} · {r.start_date} → {r.end_date}</span>
                </button>
              ))}
              {(e.reservations || []).length === 0 && <div className="text-xs text-slate-500">—</div>}
            </div>
          </div>

          {isAdmin && (
            <div className="pt-4 border-t border-white/5">
              <Button variant="destructive" size="sm" onClick={remove} data-testid="event-delete-btn"><Trash2 className="w-3.5 h-3.5 mr-1.5" />{t("delete")}</Button>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
